/**
 * 🪜 Rotinas de Liderança — Leader Standard Work (LST)
 *
 * Trabalho Padronizado dos Líderes + Gerenciamento Diário em Camadas.
 * Sub-abas: "Meu Padrão" (execução do usuário, com visão Dia/Semana/Mês),
 * "Cadeia de Ajuda" (auditoria em cascata) e "Configuração" (parametrização).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ModuleLayout } from '../../../components/layout/ModuleLayout';
import { useAppToast } from '../../../components/AppToast';
import { useAuth } from '../../../context/AuthContext';
import { useOrganizationId } from '../../../contexts/WorkspaceContext';
import { useOrgMembers, type OrgMember } from '../../../hooks/useOrgMembers';
import { usePermission } from '../../../hooks/usePermission';
import { ActionService } from '../../../services/actionService';
import { IssueService } from '../../../services/issueService';
import { useLstRibbon } from '../hooks/useLstRibbon';
import { useLstUserContext } from '../hooks/useLstUserContext';
import type { LstScreen } from '../components/LstScreenTabs';
import {
  useCreateLayerMeeting,
  useCreateRevisao,
  useCurrentWeek,
  useLstExecucao,
  useLstLayerMeetings,
  useLstMilestones,
  useLstRevisoes,
  useLstTemplates,
  useSetReportsTo,
  useTemplateMutations,
  useUpsertExecucao,
} from '../hooks/useLstData';
import { IndicatorService } from '../../../services/indicatorService';
import { scanUpcomingAlerts } from '../services/lstAlerts';
import { useQuery } from '@tanstack/react-query';
import { LstService, monthDates, monthsAgoISO, todayISO } from '../services/lstService';
import type { LstTemplateInput } from '../services/lstService';
import { buildDiaRaioX, computeChainAdherence } from '../services/lstAdherence';
import type { LstSubject } from '../services/lstAnalytics';
import { organizationStructureService } from '../../../services/admin/organizationStructureService';
import { getRoles } from '../../../services/organizationStructureService';
import {
  exportCadeiaCsv,
  exportMeuPadraoCsv,
  exportTemplatesCsv,
  exportTemplatesJson,
  exportViewToPdf,
} from '../services/lstExport';
import {
  LST_CARGO_LABELS,
  mapRoleTypeToCargo,
  resolveLstCargoFromContext,
  type LstCargo,
  type LstExecucao,
  type LstLideradoAderencia,
  type LstRotinaDoDia,
  type LstStatus,
  type LstTemplate,
} from '../types';
import { MeuPadraoSubHeader } from '../components/MeuPadraoSubHeader';
import { MeuPadraoDayView } from '../components/MeuPadraoDayView';
import {
  RotinaContextDrawer,
  type RotinaStatusSubmitPayload,
} from '../components/RotinaContextDrawer';
import { CadeiaAjudaPanel } from '../components/CadeiaAjudaPanel';
import { ConfiguracaoPanel } from '../components/ConfiguracaoPanel';
import { DesvioModal, type DesvioSubmitPayload } from '../components/DesvioModal';
import { ExecucaoIndicatorModal } from '../components/ExecucaoIndicatorModal';
import { type LstPeriodView } from '../components/PeriodSwitcher';
import { LstAnalyticsDashboard } from '../components/LstAnalyticsDashboard';
import { LstMatrixMensal } from '../components/LstMatrixMensal';
import { CadeiaMatrizMensal } from '../components/CadeiaMatrizMensal';
import { ExecucaoCellDrawer } from '../components/ExecucaoCellDrawer';
import { HourBlockDrawer } from '../components/HourBlockDrawer';
import { LstKpiDrawer } from '../components/LstKpiDrawer';
import { useTplPlannerSync } from '../hooks/useTplPlannerSync';
import { getDirectReports } from '../services/lstHierarchy';
import { TplWeeklyPlanner } from '../components/tpl/TplWeeklyPlanner';
import { TplLayerAuditPanel } from '../components/tpl/TplLayerAuditPanel';
import { TplManagementHubPanel } from '../components/tpl/TplManagementHubPanel';
import { computeTplManagementHub } from '../services/tplManagementHubInsights';
import { computeTplAdherence } from '../services/tplAdherenceAudit';
import { buildLayerAudit } from '../services/layerAuditJudgment';
import { computeDayOeeMetrics, computeTplOeeMetrics } from '../services/lstOeeMetrics';
import { computeIel } from '../services/ielService';
import { pickLinkedIndicators } from '../services/lstCorrelation';
import {
  buildAdherenceLossPareto,
  filterLstCorrectiveActions,
} from '../services/adherenceLossDiagnostic';
import { AdherenceLossDiagnosticDrawer } from '../components/cadeia/AdherenceLossDiagnosticDrawer';
import {
  normalizeLayerSections,
  resolveLayerMeetingKey,
  TPL_LAYER_DURATIONS,
} from '../services/tplLayerMeeting';
import type { HourCellData } from '../services/lstMatrixHourly';
import { isAtaColetivaContext } from '../utils/lstActivityKind';
import { resolveTemplateIndicator, templateHasLinkedIndicator } from '../utils/lstIndicator';
import { isTemplateDueOnDate } from '../services/lstSchedule';
import {
  buildRoleNameByRoleId,
  createMemberRoleNameResolver,
  filterTemplatesForUser,
  isUserScopedTemplate,
  resolveMemberLstCargo,
  templateAppliesToUser,
} from '../utils/lstTemplateScope';
import {
  isTemplateOnPlannerDay,
  sortTemplatesByPlannerTime,
} from '../services/tplPlannerBlocks';
import { resolveDisplayLstStatus } from '../utils/lstEffectiveStatus';
import { encodeMotivoParada } from '../utils/kamishibaiStatus';

interface DayDrillState {
  source: 'self' | 'chain';
  userId: string;
  cargo: LstCargo;
  memberName: string;
  date: string;
}

interface CellDrillState {
  template: LstTemplate;
  code: string;
  date: string;
  execucao: LstExecucao | null;
}

interface HourDrillState {
  cell: HourCellData;
}

const WEEKDAY_FULL = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

function formatTodayLabel(): string {
  const d = new Date();
  return `${WEEKDAY_FULL[d.getDay()]}, ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function ddmm(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

function monthLabel(): string {
  return new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
}

export const RotinasLiderancaPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const organizationId = useOrganizationId();
  const { isAdmin } = usePermission();
  const { showActionToast, showToast, ToastContainer } = useAppToast();

  const userId = user?.id ?? '';
  const today = todayISO();
  const todayLabel = useMemo(formatTodayLabel, []);
  const week = useCurrentWeek();
  const month = useMemo(() => {
    const dates = monthDates(new Date());
    return { dates, start: dates[0], end: dates[dates.length - 1] };
  }, []);

  // ── Hierarquia ────────────────────────────────────────────
  const { members, refresh: refreshMembers } = useOrgMembers(organizationId);
  const currentMember = useMemo(
    () => members.find((m) => m.id === userId) ?? null,
    [members, userId],
  );
  const liderados = useMemo(
    () => getDirectReports(userId, members),
    [members, userId],
  );
  const canAudit = liderados.length > 0;
  const canConfigure = isAdmin || currentMember?.roleType === 'manager' || currentMember?.roleType === 'director';

  // ── Estado de navegação ───────────────────────────────────
  const [screen, setScreen] = useState<LstScreen>('meu-padrao');
  const [periodView, setPeriodView] = useState<LstPeriodView>('dia');

  // ── Estado de UI (menu Exibir / Análise / drill-down) ─────
  const [compact, setCompact] = useState(false);
  const [hideDescriptions, setHideDescriptions] = useState(false);
  const [kpiDrawerOpen, setKpiDrawerOpen] = useState(false);
  const [savingNoteId, setSavingNoteId] = useState<string | null>(null);
  const [cellDrill, setCellDrill] = useState<CellDrillState | null>(null);
  const [hourDrill, setHourDrill] = useState<HourDrillState | null>(null);
  const [savingReuniao, setSavingReuniao] = useState(false);
  const [soloTaskRotina, setSoloTaskRotina] = useState<LstRotinaDoDia | null>(null);

  // Cadeia de Ajuda: alterna entre heatmap semanal e matriz mensal por liderado.
  const [chainView, setChainView] = useState<'aderencia' | 'matriz'>('aderencia');
  const [selectedLideradoId, setSelectedLideradoId] = useState<string | null>(null);
  const [selectedPadraoUserId, setSelectedPadraoUserId] = useState<string | null>(null);
  const [padraoTimelineSelectedId, setPadraoTimelineSelectedId] = useState<string | null>(null);
  const [maturityNotes, setMaturityNotes] = useState<Record<string, string>>({});
  const [adherenceDiagnostic, setAdherenceDiagnostic] = useState<DayDrillState | null>(null);

  let effectiveScreen: LstScreen = screen;
  if (screen === 'cadeia-ajuda' && !canAudit) effectiveScreen = 'meu-padrao';
  if (screen === 'auditoria' && !canAudit) effectiveScreen = 'meu-padrao';
  if (screen === ('reunioes-camada' as LstScreen)) effectiveScreen = 'auditoria';
  if (screen === 'diagnostico' && !canAudit && !canConfigure) effectiveScreen = 'meu-padrao';
  if (screen === 'configuracao' && !canConfigure) effectiveScreen = 'meu-padrao';

  const userContext = useLstUserContext(organizationId, currentMember, !!organizationId);
  const cargo = userContext.lstCargo;

  const padraoViewUserId = selectedPadraoUserId ?? userId;
  const padraoViewMember = useMemo(
    () => members.find((m) => m.id === padraoViewUserId) ?? currentMember,
    [members, padraoViewUserId, currentMember],
  );
  const viewCargo = useMemo((): LstCargo => {
    if (padraoViewUserId === userId) return cargo;
    return mapRoleTypeToCargo(padraoViewMember?.roleType);
  }, [padraoViewUserId, userId, cargo, padraoViewMember?.roleType]);

  const padraoLeaderOptions = useMemo(() => {
    if (!canAudit) return undefined;
    const self = { id: userId, name: `${currentMember?.name ?? 'Eu'} (eu)` };
    return [self, ...liderados.map((l) => ({ id: l.id, name: l.name }))];
  }, [canAudit, userId, currentMember?.name, liderados]);

  const viewingSubordinatePadrao = canAudit && padraoViewUserId !== userId;

  // ── Dados ─────────────────────────────────────────────────
  const { data: templates = [] } = useLstTemplates(organizationId);

  const { data: myExec = [] } = useLstExecucao(organizationId, userId ? [userId] : [], today, today);

  const centralGestaoEnabled = effectiveScreen === 'central-gestao';

  const { data: selfWeekExec = [] } = useLstExecucao(
    organizationId,
    centralGestaoEnabled && userId ? [userId] : [],
    week.start,
    week.end,
  );
  const meuPadraoDayEnabled = effectiveScreen === 'meu-padrao' && periodView === 'dia';
  const { data: padraoDayExec = [] } = useLstExecucao(
    organizationId,
    meuPadraoDayEnabled && padraoViewUserId ? [padraoViewUserId] : [],
    today,
    today,
  );

  const meuPadraoRangeEnabled =
    effectiveScreen === 'meu-padrao' && (periodView === 'semana' || periodView === 'mes');
  const padraoRange = periodView === 'mes' ? month : week;
  const { data: padraoRangeExec = [] } = useLstExecucao(
    organizationId,
    meuPadraoRangeEnabled && padraoViewUserId ? [padraoViewUserId] : [],
    padraoRange.start,
    padraoRange.end,
  );

  // Cadeia de Ajuda — execuções dos liderados na semana
  const lideradoIds = useMemo(() => liderados.map((l) => l.id), [liderados]);
  const { data: chainExec = [], isLoading: chainLoading } = useLstExecucao(
    organizationId,
    lideradoIds,
    week.start,
    week.end,
  );

  // Auto-auditoria (Semana/Mês) — execuções do próprio usuário no range ativo
  const selfRange = periodView === 'mes' ? month : week;
  const selfRangeEnabled =
    (effectiveScreen === 'meu-padrao' && (periodView === 'semana' || periodView === 'mes')) ||
    effectiveScreen === 'auditoria';
  const selfExecRange = effectiveScreen === 'auditoria' ? week : selfRange;
  const { data: selfExec = [] } = useLstExecucao(
    organizationId,
    selfRangeEnabled && userId ? [userId] : [],
    selfExecRange.start,
    selfExecRange.end,
  );

  // Cadeia de Ajuda → Matriz Mensal: execuções do liderado selecionado no mês.
  const effectiveLideradoId = selectedLideradoId ?? lideradoIds[0] ?? null;
  const matrizEnabled = effectiveScreen === 'cadeia-ajuda' && chainView === 'matriz';
  const { data: lideradoMonthExec = [], isLoading: lideradoMonthLoading } = useLstExecucao(
    organizationId,
    matrizEnabled && effectiveLideradoId ? [effectiveLideradoId] : [],
    month.start,
    month.end,
  );

  // ── Analytics (KPI Drawer + Dashboard) ────────────────────
  const analyticsEnabled = kpiDrawerOpen || effectiveScreen === 'diagnostico';
  const analyticsUserIds = useMemo(
    () => Array.from(new Set([userId, ...lideradoIds])).filter(Boolean),
    [userId, lideradoIds],
  );
  const analyticsStart = useMemo(() => monthsAgoISO(6), []);
  const { data: analyticsExec = [], isLoading: analyticsLoading } = useLstExecucao(
    organizationId,
    analyticsEnabled ? analyticsUserIds : [],
    analyticsStart,
    today,
  );

  const { data: departments = [] } = useQuery({
    queryKey: ['lst', 'departments', organizationId],
    queryFn: () => organizationStructureService.fetchDepartments(organizationId!),
    enabled: !!organizationId && (effectiveScreen === 'configuracao' || effectiveScreen === 'diagnostico'),
    staleTime: 300_000,
  });

  const { data: orgRoles = [] } = useQuery({
    queryKey: ['lst', 'roles', organizationId],
    queryFn: () => getRoles(organizationId!),
    enabled: !!organizationId,
    staleTime: 300_000,
  });

  const resolveMemberRoleName = useMemo(
    () => createMemberRoleNameResolver(buildRoleNameByRoleId(orgRoles)),
    [orgRoles],
  );

  const indicatorsEnabled =
    effectiveScreen === 'configuracao' ||
    effectiveScreen === 'diagnostico' ||
    effectiveScreen === 'auditoria' ||
    effectiveScreen === 'meu-padrao' ||
    centralGestaoEnabled;
  const { data: indicators = [] } = useQuery({
    queryKey: ['lst', 'indicators', organizationId],
    queryFn: () => IndicatorService.list(organizationId),
    enabled: !!organizationId && indicatorsEnabled,
    staleTime: 120_000,
  });

  const linkedIndicatorsForIel = useMemo(
    () => pickLinkedIndicators(templates.filter((t) => t.active), indicators),
    [templates, indicators],
  );

  const ielSnapshotsEnabled =
    !!organizationId &&
    linkedIndicatorsForIel.length > 0 &&
    (effectiveScreen === 'diagnostico' ||
      effectiveScreen === 'auditoria' ||
      effectiveScreen === 'meu-padrao' ||
      centralGestaoEnabled);
  const { data: ielIndicatorSnapshots = [] } = useQuery({
    queryKey: [
      'lst',
      'iel-snapshots',
      organizationId,
      linkedIndicatorsForIel.map((i) => i.id).join(','),
    ],
    queryFn: async () => {
      const batches = await Promise.all(
        linkedIndicatorsForIel.map((ind) =>
          IndicatorService.getSnapshots(organizationId!, ind.id, 200),
        ),
      );
      return batches.flat();
    },
    enabled: ielSnapshotsEnabled,
    staleTime: 60_000,
  });

  const analyticsStartDate = useMemo(() => monthsAgoISO(6), []);
  const { data: milestones = [] } = useLstMilestones(
    organizationId,
    cargo,
    analyticsStartDate,
    today,
    effectiveScreen === 'diagnostico',
  );

  const myCargoTemplates = useMemo(
    () => filterTemplatesForUser(templates, cargo, userId),
    [templates, cargo, userId],
  );

  const selfAdherencePct = useMemo(
    () => computeTplAdherence(myCargoTemplates, selfExec, week.dates).adherencePct,
    [myCargoTemplates, selfExec, week.dates],
  );

  const execDashboardLeaders = useMemo(
    () =>
      liderados.map((m) => ({
        userId: m.id,
        name: m.name,
        cargo: LST_CARGO_LABELS[mapRoleTypeToCargo(m.roleType)],
      })),
    [liderados],
  );

  const leaderExecMap = useMemo(() => {
    const map = new Map<string, LstExecucao[]>();
    for (const ex of analyticsExec) {
      const arr = map.get(ex.user_id) ?? [];
      arr.push(ex);
      map.set(ex.user_id, arr);
    }
    return map;
  }, [analyticsExec]);

  const layerSubordinate = useMemo(
    () => liderados.find((l) => l.id === effectiveLideradoId) ?? liderados[0] ?? null,
    [liderados, effectiveLideradoId],
  );

  const leaderCargo = useMemo(
    () => resolveLstCargoFromContext(cargo, userContext.roleName),
    [cargo, userContext.roleName],
  );

  const subordinateCargo = useMemo(() => {
    if (!layerSubordinate) return 'lider' as LstCargo;
    return resolveLstCargoFromContext(
      mapRoleTypeToCargo(layerSubordinate.roleType),
      null,
    );
  }, [layerSubordinate]);

  const layerMeetingKey = useMemo(() => {
    if (!layerSubordinate) return 'lider-supervisor' as const;
    return resolveLayerMeetingKey(subordinateCargo, leaderCargo);
  }, [layerSubordinate, subordinateCargo, leaderCargo]);

  const upsert = useUpsertExecucao(organizationId);
  const templateMut = useTemplateMutations(organizationId);
  const setReportsMut = useSetReportsTo(organizationId);
  const createRevisaoMut = useCreateRevisao(organizationId ?? '');
  const createLayerMeetingMut = useCreateLayerMeeting(organizationId ?? '');

  const layerMeetingsEnabled = effectiveScreen === 'auditoria' && !!organizationId;
  const { data: layerMeetings = [] } = useLstLayerMeetings(
    organizationId ?? '',
    {
      leaderUserId: userId,
      subordinateUserId: effectiveLideradoId ?? undefined,
      limit: 10,
    },
    layerMeetingsEnabled,
  );

  const drawerAtaEnabled =
    (!!soloTaskRotina && isAtaColetivaContext(soloTaskRotina.template)) ||
    (!!cellDrill && isAtaColetivaContext(cellDrill.template));
  const { data: revisoes = [], isLoading: revisoesLoading } = useLstRevisoes(
    organizationId ?? '',
    !!organizationId && drawerAtaEnabled,
  );

  const plannerSyncCargo = effectiveScreen === 'construtor' ? cargo : viewCargo;
  const persistPlannerTime = useCallback(
    (templateId: string, scheduledTime: string) => {
      void templateMut.update.mutateAsync({
        id: templateId,
        patch: { scheduled_time: scheduledTime },
      });
    },
    [templateMut.update],
  );
  const { blocks: plannerBlocks, moveBlock: movePlannerBlock } = useTplPlannerSync({
    templates,
    cargo: plannerSyncCargo,
    userId: effectiveScreen === 'construtor' ? userId : padraoViewUserId,
    weekDates: week.dates,
    debugConsistency: effectiveScreen === 'construtor',
    onPersistTime: persistPlannerTime,
  });

  // ── Meu Padrão (Dia): merge template × execução ──────────
  const rotinas: LstRotinaDoDia[] = useMemo(() => {
    const execByTemplate = new Map(padraoDayExec.map((e) => [e.template_id, e]));
    return templates
      .filter(
        (t) =>
          templateAppliesToUser(t, viewCargo, padraoViewUserId) &&
          isTemplateDueOnDate(t, today, month.dates) &&
          (isUserScopedTemplate(t) || isTemplateOnPlannerDay(t, plannerBlocks, today)),
      )
      .sort((a, b) => sortTemplatesByPlannerTime(a, b, plannerBlocks))
      .map((template) => {
        const execucao = execByTemplate.get(template.id) ?? null;
        return { template, execucao, status: execucao?.status ?? 'nao_realizado' };
      });
  }, [templates, padraoDayExec, viewCargo, padraoViewUserId, today, month.dates, plannerBlocks]);

  useEffect(() => {
    setPadraoTimelineSelectedId(null);
  }, [today, padraoViewUserId, periodView]);

  const upcomingAlerts = useMemo(() => scanUpcomingAlerts(rotinas), [rotinas]);
  const pushedAlertsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    for (const alert of upcomingAlerts) {
      if (alert.channel !== 'push' && alert.channel !== 'both') continue;
      const key = `${alert.templateId}_${alert.scheduledTime}`;
      if (pushedAlertsRef.current.has(key)) continue;
      pushedAlertsRef.current.add(key);
      showToast(
        alert.isCritical ? 'warning' : 'info',
        `${alert.title} em ${alert.minutesUntil} min (${alert.scheduledTime})`,
      );
    }
  }, [upcomingAlerts, showToast]);

  // ── Auto-auditoria: aderência diária do próprio usuário ──
  const selfMember: OrgMember = useMemo(
    () =>
      currentMember ?? {
        id: userId,
        name: user?.name ?? '',
        email: user?.email ?? '',
        reportsToUserId: null,
        roleType: null,
        departmentId: null,
        roleId: null,
      },
    [currentMember, userId, user?.name, user?.email],
  );

  const selfDias = useMemo(() => {
    if (!selfRangeEnabled) return [];
    const rows = computeChainAdherence({
      members: [selfMember],
      templates,
      execucoes: selfExec,
      weekDates: selfRange.dates,
      resolveMemberRoleName,
    });
    return rows[0]?.dias ?? [];
  }, [selfRangeEnabled, selfMember, templates, selfExec, selfRange.dates]);

  // ── Cadeia de Ajuda: matriz de aderência ─────────────────
  const chainRows = useMemo(
    () =>
      computeChainAdherence({
        members: liderados,
        templates,
        execucoes: chainExec,
        weekDates: week.dates,
        resolveMemberRoleName,
      }),
    [liderados, templates, chainExec, week.dates, resolveMemberRoleName],
  );

  const hubChainRows = useMemo(() => {
    if (canAudit) return chainRows ?? [];
    if (!currentMember) return [];
    return computeChainAdherence({
      members: [currentMember],
      templates,
      execucoes: selfWeekExec,
      weekDates: week.dates,
      resolveMemberRoleName,
    });
  }, [canAudit, chainRows, currentMember, templates, selfWeekExec, week.dates, resolveMemberRoleName]);

  const hubLeaders = useMemo(
    () =>
      canAudit
        ? liderados.map((m) => ({
            id: m.id,
            name: m.name,
            cargo: mapRoleTypeToCargo(m.roleType),
          }))
        : currentMember
          ? [{ id: userId, name: currentMember.name, cargo }]
          : [],
    [canAudit, liderados, currentMember, userId, cargo],
  );

  const hubExecucoes = useMemo(() => {
    const seen = new Set<string>();
    const merged: LstExecucao[] = [];
    for (const e of [...chainExec, ...selfWeekExec]) {
      const k = `${e.template_id}__${e.user_id}__${e.data}`;
      if (seen.has(k)) continue;
      seen.add(k);
      merged.push(e);
    }
    return merged;
  }, [chainExec, selfWeekExec]);

  const auditLeaders = useMemo(
    () =>
      liderados.map((m) => ({
        id: m.id,
        name: m.name,
        cargo: mapRoleTypeToCargo(m.roleType),
      })),
    [liderados],
  );

  const auditLeaderTemplates = useMemo(() => {
    if (!layerSubordinate) return [];
    const c = resolveMemberLstCargo(layerSubordinate, resolveMemberRoleName);
    return templates.filter(
      (t) => t.active && templateAppliesToUser(t, c, layerSubordinate.id),
    );
  }, [templates, layerSubordinate, resolveMemberRoleName]);

  const auditLeaderExec = useMemo(
    () => chainExec.filter((e) => e.user_id === effectiveLideradoId),
    [chainExec, effectiveLideradoId],
  );

  const auditLeaderOee = useMemo(
    () => computeTplOeeMetrics(auditLeaderTemplates, auditLeaderExec, week.dates),
    [auditLeaderTemplates, auditLeaderExec, week.dates],
  );

  const handleApprovePattern = useCallback(async () => {
    if (!organizationId || !userId || !layerSubordinate) {
      showToast('error', 'Selecione um líder para aprovar o padrão.');
      return;
    }
    const note = maturityNotes[layerSubordinate.id] ?? '';
    const duration = TPL_LAYER_DURATIONS[layerMeetingKey];
    const judgment = buildLayerAudit({
      templates: auditLeaderTemplates,
      execucoes: auditLeaderExec,
      dates: week.dates,
      today,
      indicators,
    });
    const formatAuditPct = (value: number | null) => (value == null ? 'sem avaliação' : `${value}%`);
    const sections = normalizeLayerSections({
      aderencia: `Disciplina do líder: ${formatAuditPct(judgment.disciplinePct)}`,
      resultados: `Conformidade do operador: ${formatAuditPct(judgment.conformityPct)}. Resultado da meta: ${formatAuditPct(judgment.resultPct)}.`,
      obstaculos: note || '—',
      escalonamentos: '',
      'plano-acao': note ? `Nota da auditoria: ${note}` : 'Auditoria concluída sem ressalvas.',
    });
    const result = await createLayerMeetingMut.mutateAsync({
      layer_key: layerMeetingKey,
      leader_user_id: userId,
      subordinate_user_id: layerSubordinate.id,
      leader_cargo: leaderCargo,
      subordinate_cargo: subordinateCargo,
      adherence_pct: judgment.disciplinePct ?? 0,
      duration_minutes: duration?.maxMinutes ?? null,
      sections,
      actions: [],
      created_by: userId,
    });
    if (!result) {
      showToast('error', 'Não foi possível registrar a aprovação do padrão.');
      return;
    }
    showToast('success', `Padrão de ${layerSubordinate.name} aprovado com sucesso.`);
  }, [
    organizationId,
    userId,
    layerSubordinate,
    maturityNotes,
    layerMeetingKey,
    leaderCargo,
    subordinateCargo,
    auditLeaderTemplates,
    auditLeaderExec,
    week.dates,
    today,
    indicators,
    createLayerMeetingMut,
    showToast,
  ]);

  const lastFieldAudit = layerMeetings[0] ?? null;

  const diagnosticEnabled = !!adherenceDiagnostic && !!organizationId;
  const diagnosticoEnabled = effectiveScreen === 'diagnostico';
  const auditActionsEnabled = effectiveScreen === 'auditoria' && !!organizationId;
  const cadeiaEnabled = effectiveScreen === 'cadeia-ajuda' && !!organizationId;
  const hubActionsEnabled = centralGestaoEnabled && !!organizationId;
  const fullAnalyticsEnabled = diagnosticoEnabled;
  const { data: orgActions = [], isLoading: actionsLoading } = useQuery({
    queryKey: ['lst', 'corrective-actions', organizationId],
    queryFn: () => ActionService.list(organizationId!),
    enabled:
      !!organizationId &&
      (diagnosticEnabled || fullAnalyticsEnabled || auditActionsEnabled || cadeiaEnabled || hubActionsEnabled),
    staleTime: 60_000,
  });

  const diagnosticRow = useMemo(() => {
    if (!adherenceDiagnostic) return null;
    return chainRows.find((r) => r.member.id === adherenceDiagnostic.userId) ?? null;
  }, [adherenceDiagnostic, chainRows]);

  const diagnosticOee = useMemo(() => {
    if (!adherenceDiagnostic) return null;
    const dayExec = chainExec.filter(
      (e) => e.user_id === adherenceDiagnostic.userId && e.data === adherenceDiagnostic.date,
    );
    const leaderTemplates = templates.filter(
      (t) =>
        t.active &&
        templateAppliesToUser(t, adherenceDiagnostic.cargo, adherenceDiagnostic.userId),
    );
    return computeTplOeeMetrics(leaderTemplates, dayExec, [adherenceDiagnostic.date]);
  }, [adherenceDiagnostic, chainExec, templates]);

  const diagnosticPareto = useMemo(() => {
    if (!adherenceDiagnostic || !diagnosticRow) return [];
    const weekOcc = diagnosticRow.ocorrencias.filter((o) => o.date === adherenceDiagnostic.date);
    if (weekOcc.length > 0) return buildAdherenceLossPareto(weekOcc);
    return buildAdherenceLossPareto(diagnosticRow.ocorrencias);
  }, [adherenceDiagnostic, diagnosticRow]);

  const diagnosticActions = useMemo(() => {
    if (!adherenceDiagnostic) return [];
    return filterLstCorrectiveActions(orgActions, adherenceDiagnostic.userId);
  }, [adherenceDiagnostic, orgActions]);

  const diagnosticRaioX = useMemo(() => {
    if (!adherenceDiagnostic) return null;
    return buildDiaRaioX({
      cargo: adherenceDiagnostic.cargo,
      templates,
      execucoes: chainExec,
      userId: adherenceDiagnostic.userId,
      date: adherenceDiagnostic.date,
    });
  }, [adherenceDiagnostic, templates, chainExec]);

  const managementHub = useMemo(
    () =>
      computeTplManagementHub({
        templates,
        execucoes: hubExecucoes,
        weekDates: week.dates,
        monthDates: month.dates,
        today,
        viewerCargo: cargo,
        chainRows: hubChainRows,
        leaders: hubLeaders,
        orgActions,
        indicators,
        indicatorSnapshots: ielIndicatorSnapshots,
      }),
    [
      templates,
      hubExecucoes,
      week.dates,
      month.dates,
      today,
      cargo,
      hubChainRows,
      hubLeaders,
      orgActions,
      indicators,
      ielIndicatorSnapshots,
    ],
  );

  // ── Handlers: execução ────────────────────────────────────
  const [busyTemplateId, setBusyTemplateId] = useState<string | null>(null);
  const [desvioRotina, setDesvioRotina] = useState<LstRotinaDoDia | null>(null);
  const [execucaoIndicatorRotina, setExecucaoIndicatorRotina] = useState<LstRotinaDoDia | null>(null);

  const saveExecucao = useCallback(
    async (
      rotina: LstRotinaDoDia,
      status: LstRotinaDoDia['status'],
      extra?: {
        justificativa_desvio?: string | null;
        action_id?: string | null;
        indicator_realized_value?: number | null;
        observacao?: string | null;
      },
      execDate?: string,
    ) => {
      if (!organizationId || !userId) return null;
      setBusyTemplateId(rotina.template.id);
      try {
        return await upsert.mutateAsync({
          organization_id: organizationId,
          template_id: rotina.template.id,
          user_id: userId,
          data: execDate ?? today,
          status,
          justificativa_desvio: extra?.justificativa_desvio ?? rotina.execucao?.justificativa_desvio ?? null,
          action_id: extra?.action_id ?? rotina.execucao?.action_id ?? null,
          indicator_realized_value:
            extra?.indicator_realized_value !== undefined
              ? extra.indicator_realized_value
              : rotina.execucao?.indicator_realized_value ?? null,
          observacao: extra?.observacao ?? rotina.execucao?.observacao ?? null,
        });
      } finally {
        setBusyTemplateId(null);
      }
    },
    [organizationId, userId, today, upsert],
  );

  const resolveDisplayStatus = useCallback(
    (rotina: LstRotinaDoDia) =>
      resolveDisplayLstStatus(rotina.template, rotina.execucao, today, today),
    [today],
  );

  const activeDrawerRotina = useMemo((): LstRotinaDoDia | null => {
    if (soloTaskRotina) return soloTaskRotina;
    if (cellDrill && isAtaColetivaContext(cellDrill.template)) {
      return {
        template: cellDrill.template,
        execucao: cellDrill.execucao,
        status: cellDrill.execucao?.status ?? 'nao_realizado',
      };
    }
    return null;
  }, [soloTaskRotina, cellDrill]);

  const drawerExecDate = soloTaskRotina ? today : cellDrill?.date ?? today;

  const drawerDisplayStatus = useMemo((): LstStatus | 'pendente' => {
    if (!activeDrawerRotina) return 'pendente';
    return resolveDisplayLstStatus(
      activeDrawerRotina.template,
      activeDrawerRotina.execucao,
      drawerExecDate,
      today,
    );
  }, [activeDrawerRotina, drawerExecDate, today]);

  const drawerPeriod = useMemo(() => {
    if (periodView === 'mes') {
      return { start: month.start, end: month.end, exec: padraoRangeExec };
    }
    if (periodView === 'semana') {
      return { start: week.start, end: week.end, exec: padraoRangeExec };
    }
    return { start: today, end: today, exec: padraoDayExec };
  }, [periodView, month, week, today, padraoDayExec, padraoRangeExec]);

  const desviosDoDia = useMemo(
    () =>
      rotinas
        .filter((r) => resolveDisplayStatus(r) === 'realizado_com_desvio')
        .map((r) => ({
          title: r.template.title,
          justificativa: r.execucao?.justificativa_desvio ?? null,
          isCritical: r.template.is_critical,
        })),
    [rotinas, resolveDisplayStatus],
  );

  const meuPadraoStatusById = useMemo(() => {
    const map = new Map<string, LstStatus | 'pendente'>();
    for (const r of rotinas) {
      map.set(r.template.id, resolveDisplayStatus(r));
    }
    return map;
  }, [rotinas, resolveDisplayStatus]);

  const meuPadraoOee = useMemo(() => {
    if (effectiveScreen !== 'meu-padrao' || periodView !== 'dia') return null;
    const withEffective = rotinas.map((r) => ({
      status: (() => {
        const d = resolveDisplayStatus(r);
        return d === 'pendente' ? ('nao_realizado' as const) : d;
      })(),
    }));
    return computeDayOeeMetrics(withEffective);
  }, [effectiveScreen, periodView, rotinas, resolveDisplayStatus]);

  const meuPadraoIel = useMemo(() => {
    if (effectiveScreen !== 'meu-padrao' || periodView !== 'dia' || viewingSubordinatePadrao) return null;
    const dayTemplates = rotinas.map((r) => r.template);
    if (dayTemplates.length === 0) return null;
    return computeIel({
      templates: dayTemplates,
      execucoes: padraoDayExec,
      dates: [today],
      indicators,
      indicatorSnapshots: ielIndicatorSnapshots,
      userId: padraoViewUserId,
      includeTrend: false,
    });
  }, [
    effectiveScreen,
    periodView,
    viewingSubordinatePadrao,
    rotinas,
    padraoDayExec,
    today,
    indicators,
    ielIndicatorSnapshots,
    padraoViewUserId,
  ]);

  const handleDrawerSubmitStatus = useCallback(
    async (payload: RotinaStatusSubmitPayload): Promise<boolean> => {
      if (!activeDrawerRotina || !organizationId || !userId) return false;
      if (viewingSubordinatePadrao) {
        showToast('info', 'Modo visualização — registros são somente leitura.');
        return false;
      }
      const rotina = activeDrawerRotina;

      let actionId: string | null = rotina.execucao?.action_id ?? null;
      let justificativa: string | null = payload.justificativaDesvio ?? null;

      if (payload.status === 'realizado_com_desvio') {
        justificativa =
          payload.justificativaDesvio ??
          payload.quickAction?.whatToDo ??
          payload.criticalProblem?.summary ??
          null;
      } else if (payload.status === 'processo_parado' && payload.motivoParada) {
        justificativa = encodeMotivoParada(payload.motivoParada);
      } else if (payload.status === 'realizado') {
        justificativa = null;
      }

      if (payload.quickAction) {
        const action = await ActionService.create({
          organization_id: organizationId,
          title: `Ação TPL: ${rotina.template.title}`,
          description: payload.quickAction.whatToDo,
          status: 'pending',
          priority: rotina.template.is_critical ? 'high' : 'medium',
          assigned_to: payload.quickAction.assignee,
          created_by: userId,
          metadata: {
            source: 'lst',
            lst_template_id: rotina.template.id,
            lst_cargo: cargo,
            lst_date: drawerExecDate,
          },
        });
        actionId = action?.id ?? null;
      }

      if (payload.criticalProblem) {
        try {
          const issue = await IssueService.create(organizationId, {
            title: `Desvio TPL: ${rotina.template.title}`,
            description: payload.criticalProblem.summary,
            type: 'deviation',
            severity: rotina.template.is_critical ? 'critical' : 'high',
            mode: 'structured_problem',
            status: 'open',
            assigned_to: payload.criticalProblem.assignee,
            created_by: userId,
            source: 'lst_tpl',
            tags: ['tpl', 'kamishibai', rotina.template.id],
          });
          justificativa = `[MASP #${issue.id.slice(0, 8)}] ${payload.criticalProblem.summary}`;
        } catch {
          showToast('error', 'Não foi possível abrir o problema na Central MASP.');
          return false;
        }
      }

      const result = await saveExecucao(
        rotina,
        payload.status,
        {
          justificativa_desvio: justificativa,
          action_id: actionId,
          indicator_realized_value: payload.indicatorRealizedValue ?? null,
          observacao: payload.observacao ?? rotina.execucao?.observacao ?? null,
        },
        drawerExecDate,
      );

      if (!result) {
        showToast('error', 'Não foi possível registrar a verificação.');
        return false;
      }

      if (payload.criticalProblem) {
        showActionToast('warning', 'Desvio registrado e problema aberto no MASP.', [
          { label: 'Abrir Central de Problemas', onClick: () => navigate('/problemas'), variant: 'primary' },
        ]);
      } else if (payload.quickAction && actionId) {
        showActionToast('success', 'Desvio registrado e ação rápida criada.', [
          { label: 'Abrir Central de Ações', onClick: () => navigate('/acoes'), variant: 'primary' },
        ]);
      } else if (payload.status === 'realizado_com_desvio') {
        showActionToast('warning', 'Desvio registrado (Δ).', [
          { label: 'Abrir MASP', onClick: () => navigate('/problemas'), variant: 'primary' },
        ]);
      } else if (payload.status === 'processo_parado') {
        showToast('success', 'Exceção "Processo Parado" registrada.');
      } else {
        showToast('success', 'Status atualizado.');
      }

      setSoloTaskRotina((prev) =>
        prev?.template.id === rotina.template.id
          ? { ...prev, execucao: result, status: payload.status }
          : prev,
      );
      setCellDrill((prev) =>
        prev?.template.id === rotina.template.id && prev.date === drawerExecDate
          ? { ...prev, execucao: result }
          : prev,
      );
      return true;
    },
    [
      activeDrawerRotina,
      organizationId,
      userId,
      viewingSubordinatePadrao,
      drawerExecDate,
      cargo,
      saveExecucao,
      showToast,
      showActionToast,
      navigate,
    ],
  );

  const handleSaveAtaFromDrawer = useCallback(
    async (payload: { notes: string; lideradoUserId: string | null; markRealizado: boolean }) => {
      if (!activeDrawerRotina || !organizationId || !userId) return false;
      setSavingReuniao(true);
      try {
        const revisao = await createRevisaoMut.mutateAsync({
          target_role: cargo,
          liderado_user_id: payload.lideradoUserId,
          reviewer_user_id: userId,
          notes: payload.notes,
          label: `Reunião de Camada — ${drawerExecDate}`,
        });
        if (!revisao) {
          showToast('error', 'Não foi possível registrar a ata.');
          return false;
        }
        if (payload.markRealizado) {
          await saveExecucao(
            activeDrawerRotina,
            'realizado',
            { observacao: payload.notes, justificativa_desvio: null },
            drawerExecDate,
          );
        }
        showToast('success', 'Ata da reunião registrada.');
        return true;
      } finally {
        setSavingReuniao(false);
      }
    },
    [activeDrawerRotina, organizationId, userId, cargo, drawerExecDate, createRevisaoMut, saveExecucao, showToast],
  );

  const closeContextDrawer = useCallback(() => {
    setSoloTaskRotina(null);
    if (cellDrill && isAtaColetivaContext(cellDrill.template)) {
      setCellDrill(null);
    }
  }, [cellDrill]);

  const handleMarcarRealizado = useCallback(
    async (rotina: LstRotinaDoDia) => {
      if (!organizationId || !userId) return;

      if (rotina.status === 'realizado') {
        const result = await saveExecucao(rotina, 'nao_realizado', {
          justificativa_desvio: null,
          indicator_realized_value: null,
        });
        if (!result) showToast('error', 'Não foi possível salvar a rotina.');
        return;
      }

      if (templateHasLinkedIndicator(rotina.template)) {
        setExecucaoIndicatorRotina(rotina);
        return;
      }

      const result = await saveExecucao(rotina, 'realizado', { justificativa_desvio: null });
      if (!result) showToast('error', 'Não foi possível salvar a rotina.');
    },
    [organizationId, userId, saveExecucao, showToast],
  );

  const handleSubmitIndicatorExecucao = useCallback(
    async (payload: { indicatorRealizedValue: number }) => {
      if (!execucaoIndicatorRotina) return;
      const result = await saveExecucao(execucaoIndicatorRotina, 'realizado', {
        justificativa_desvio: null,
        indicator_realized_value: payload.indicatorRealizedValue,
      });
      setExecucaoIndicatorRotina(null);
      if (!result) showToast('error', 'Não foi possível salvar a rotina.');
      else showToast('success', 'Rotina realizada com valor do indicador registrado.');
    },
    [execucaoIndicatorRotina, saveExecucao, showToast],
  );

  const handleSaveDrawerIndicator = useCallback(
    async (value: number): Promise<boolean> => {
      if (!cellDrill || !organizationId || !userId) return false;
      const currentStatus = cellDrill.execucao?.status ?? 'nao_realizado';
      const nextStatus = currentStatus === 'nao_realizado' ? 'realizado' : currentStatus;
      const rotina: LstRotinaDoDia = {
        template: cellDrill.template,
        execucao: cellDrill.execucao,
        status: currentStatus,
      };
      const result = await saveExecucao(rotina, nextStatus, {
        indicator_realized_value: value,
        justificativa_desvio: cellDrill.execucao?.justificativa_desvio ?? null,
      });
      if (!result) {
        showToast('error', 'Não foi possível salvar o valor realizado.');
        return false;
      }
      setCellDrill((prev) => (prev ? { ...prev, execucao: result } : null));
      showToast('success', 'Valor realizado registrado.');
      return true;
    },
    [cellDrill, organizationId, userId, saveExecucao, showToast],
  );

  const handleSubmitDesvio = useCallback(
    async (payload: DesvioSubmitPayload) => {
      if (!desvioRotina || !organizationId || !userId) return;
      const rotina = desvioRotina;

      let actionId: string | null = null;
      if (payload.gerarAcao) {
        const action = await ActionService.create({
          organization_id: organizationId,
          title: `Desvio LST: ${rotina.template.title}`,
          description: payload.justificativa,
          status: 'pending',
          priority: rotina.template.is_critical ? 'high' : 'medium',
          assigned_to: userId,
          created_by: userId,
          metadata: {
            source: 'lst',
            lst_template_id: rotina.template.id,
            lst_cargo: cargo,
            lst_date: today,
          },
        });
        actionId = action?.id ?? null;
      }

      const result = await saveExecucao(rotina, 'realizado_com_desvio', {
        justificativa_desvio: payload.justificativa,
        action_id: actionId,
        indicator_realized_value: payload.indicatorRealizedValue ?? null,
      });

      setDesvioRotina(null);

      if (!result) {
        showToast('error', 'Não foi possível registrar o desvio.');
        return;
      }
      if (payload.gerarAcao && actionId) {
        showActionToast('warning', 'Desvio registrado e pendência criada.', [
          { label: 'Abrir Central de Ações', onClick: () => navigate('/acoes'), variant: 'primary' },
        ]);
      } else {
        showToast('success', 'Desvio registrado.');
      }
    },
    [desvioRotina, organizationId, userId, today, cargo, saveExecucao, navigate, showToast, showActionToast],
  );

  const desvioIndicatorMeta = useMemo(() => {
    if (!desvioRotina) return null;
    return resolveTemplateIndicator(desvioRotina.template, indicators);
  }, [desvioRotina, indicators]);

  const execucaoIndicatorMeta = useMemo(() => {
    if (!execucaoIndicatorRotina) return null;
    return resolveTemplateIndicator(execucaoIndicatorRotina.template, indicators);
  }, [execucaoIndicatorRotina, indicators]);

  // ── Handlers: configuração ───────────────────────────────
  const handleCreateTemplate = useCallback(
    async (input: LstTemplateInput) => {
      const result = await templateMut.create.mutateAsync({
        ...input,
        organization_id: organizationId,
        created_by: userId,
      });
      if (!result) showToast('error', 'Não foi possível criar a rotina.');
      else showToast('success', 'Rotina criada.');
      return !!result;
    },
    [templateMut.create, organizationId, userId, showToast],
  );

  const handleUpdateTemplate = useCallback(
    async (id: string, patch: Partial<LstTemplateInput>) => {
      const rest: Partial<LstTemplateInput> = { ...patch };
      delete rest.organization_id;
      const result = await templateMut.update.mutateAsync({ id, patch: rest });
      if (!result) showToast('error', 'Não foi possível atualizar a rotina.');
      else showToast('success', 'Rotina atualizada.');
      return !!result;
    },
    [templateMut.update, showToast],
  );

  const handleDeactivateTemplate = useCallback(
    async (id: string) => {
      const ok = await templateMut.deactivate.mutateAsync(id);
      if (ok) showToast('success', 'Rotina removida.');
      else showToast('error', 'Não foi possível remover a rotina.');
      return ok;
    },
    [templateMut.deactivate, showToast],
  );

  const [savingLink, setSavingLink] = useState<string | null>(null);
  const handleSetReportsTo = useCallback(
    async (targetUserId: string, reportsToUserId: string | null) => {
      setSavingLink(targetUserId);
      const ok = await setReportsMut.mutateAsync({ userId: targetUserId, reportsToUserId });
      setSavingLink(null);
      if (ok) {
        await refreshMembers();
        showToast('success', 'Vínculo atualizado.');
      } else {
        showToast('error', 'Não foi possível atualizar o vínculo.');
      }
      return ok;
    },
    [setReportsMut, refreshMembers, showToast],
  );

  // ── Diagnóstico de perda de aderência (Cadeia) ─────────────
  const handleOpenAdherenceDiagnostic = useCallback((row: LstLideradoAderencia, date: string) => {
    setAdherenceDiagnostic({
      source: 'chain',
      userId: row.member.id,
      cargo: row.cargo,
      memberName: row.member.name,
      date,
    });
  }, []);

  const resolveExecutorName = useCallback(
    (uid: string): string | null => {
      const m = members.find((mb) => mb.id === uid);
      if (m) return m.name;
      if (uid === userId) return user?.name ?? null;
      return null;
    },
    [members, userId, user?.name],
  );

  const handleCellClick = useCallback(
    (template: LstTemplate, date: string, execucao: LstExecucao | null, code: string) => {
      if (date === today) {
        setSoloTaskRotina({
          template,
          execucao,
          status: execucao?.status ?? 'nao_realizado',
        });
        return;
      }
      setCellDrill({ template, code, date, execucao });
    },
    [today],
  );

  const standardDrillOpen = !!cellDrill && !isAtaColetivaContext(cellDrill.template);

  const handleHourBlockClick = useCallback((cell: HourCellData) => {
    setHourDrill({ cell });
  }, []);

  // ── KPI drawer usa hubChainRows + rotinas do dia ─────────
  const analyticsSubjects: LstSubject[] = useMemo(() => {
    const self: LstSubject = {
      userId,
      cargo,
      name: user?.name ?? currentMember?.name ?? 'Eu',
      member: selfMember,
    };
    const others: LstSubject[] = liderados.map((l) => ({
      userId: l.id,
      cargo: mapRoleTypeToCargo(l.roleType),
      name: l.name,
      member: l,
    }));
    return [self, ...others];
  }, [userId, cargo, user?.name, currentMember?.name, selfMember, liderados]);

  // ── SOP / Memória de Campo: salvar nota + foto ────────────
  const handleSaveNote = useCallback(
    async (rotina: LstRotinaDoDia, observacao: string, file: File | null) => {
      if (!organizationId || !userId) return;
      setSavingNoteId(rotina.template.id);
      let anexoUrl: string | null = null;
      if (file) {
        anexoUrl = await LstService.uploadAnexo(organizationId, rotina.template.id, file);
        if (!anexoUrl) showToast('error', 'Falha ao enviar a foto (verifique o bucket de anexos).');
      }
      const result = await upsert.mutateAsync({
        organization_id: organizationId,
        template_id: rotina.template.id,
        user_id: userId,
        data: today,
        status: rotina.status,
        justificativa_desvio: rotina.execucao?.justificativa_desvio ?? null,
        observacao: observacao || null,
        ...(anexoUrl ? { anexo_url: anexoUrl } : {}),
      });
      setSavingNoteId(null);
      if (result) showToast('success', 'Observação registrada na rotina.');
      else showToast('error', 'Não foi possível salvar a observação.');
    },
    [organizationId, userId, today, upsert, showToast],
  );

  // ── Handlers: Arquivo / Exportação ────────────────────────
  const handleSalvarPadrao = useCallback(() => {
    showToast('success', 'Padrão salvo. As execuções são persistidas automaticamente.');
  }, [showToast]);

  const handleSalvarComo = useCallback(() => {
    if (templates.length === 0) {
      showToast('error', 'Não há padrões cadastrados para duplicar.');
      return;
    }
    exportTemplatesJson(templates);
    showToast('success', 'Matriz de padrões exportada (JSON).');
  }, [templates, showToast]);

  const handleExportExcel = useCallback(() => {
    if (effectiveScreen === 'cadeia-ajuda') {
      exportCadeiaCsv(chainRows, week.dates);
    } else if (effectiveScreen === 'configuracao') {
      const memberNameById = new Map(members.map((m) => [m.id, m.name]));
      exportTemplatesCsv(templates, memberNameById);
    } else {
      exportMeuPadraoCsv(rotinas, today);
    }
    showToast('success', 'Exportação Excel (CSV) gerada.');
  }, [effectiveScreen, chainRows, week.dates, templates, members, rotinas, today, showToast]);

  const handleExportPdf = useCallback(() => {
    exportViewToPdf();
  }, []);

  const handleOpenMeuPadraoTask = useCallback((rotina: LstRotinaDoDia) => {
    setSoloTaskRotina(rotina);
  }, []);

  const meuPadraoRoleLabel = useMemo(() => {
    if (padraoViewUserId === userId) {
      return userContext.roleName || LST_CARGO_LABELS[cargo];
    }
    const name = padraoViewMember?.name ?? 'Liderado';
    return `${name} · ${LST_CARGO_LABELS[viewCargo]}`;
  }, [padraoViewUserId, userId, userContext.roleName, cargo, padraoViewMember?.name, viewCargo]);

  const meuPadraoDateLabel = useMemo(() => {
    if (periodView === 'dia') return todayLabel;
    if (periodView === 'semana') return `${ddmm(week.start)} – ${ddmm(week.end)}`;
    if (periodView === 'mes') {
      const d = month.dates[0];
      return d ? new Date(d + 'T12:00:00').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }) : todayLabel;
    }
    return 'Carga horária';
  }, [periodView, todayLabel, week.start, week.end, month.dates]);

  const handlePadraoLeaderChange = useCallback((id: string) => {
    setSelectedPadraoUserId(id === userId ? null : id);
  }, [userId]);

  // ── Ribbon ────────────────────────────────────────────────
  const ribbonConfig = useLstRibbon({
    screen: effectiveScreen,
    onScreenChange: setScreen,
    canAudit,
    canConfigure,
    onSalvarPadrao: handleSalvarPadrao,
    onSalvarComo: handleSalvarComo,
    onExportPdf: handleExportPdf,
    onExportExcel: handleExportExcel,
    compact,
    onToggleCompact: () => setCompact((v) => !v),
    hideDescriptions,
    onToggleHideDescriptions: () => setHideDescriptions((v) => !v),
    onOpenKpi: () => setKpiDrawerOpen(true),
    showPeriod: false,
    periodView,
    onPeriodChange: setPeriodView,
  });

  const savingTemplate = templateMut.create.isPending || templateMut.update.isPending;

  return (
    <ModuleLayout ribbonConfig={ribbonConfig}>
      <div className="flex h-full min-h-0 flex-col bg-[var(--bg-app)] px-3 pt-3">
        {effectiveScreen === 'central-gestao' && (
          <TplManagementHubPanel
            data={managementHub}
            loading={chainLoading && canAudit}
            weekLabel={`${ddmm(week.start)} – ${ddmm(week.end)}`}
            onNavigate={setScreen}
          />
        )}

        {effectiveScreen === 'meu-padrao' && (
          <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-[var(--bg-surface)]">
            {periodView === 'dia' ? (
              <>
                <MeuPadraoSubHeader
                  periodView={periodView}
                  onPeriodChange={setPeriodView}
                  roleLabel={meuPadraoRoleLabel}
                  departmentLabel={userContext.departmentName}
                  dateLabel={meuPadraoDateLabel}
                  leaderOptions={padraoLeaderOptions}
                  selectedLeaderId={padraoViewUserId}
                  onLeaderChange={handlePadraoLeaderChange}
                  viewingSubordinate={viewingSubordinatePadrao}
                />
                <div className="min-h-0 flex-1 overflow-hidden px-3 pb-2 pt-2">
                  {rotinas.length === 0 ? (
                    <NoTemplates cargoLabel={LST_CARGO_LABELS[viewCargo]} />
                  ) : (
                    <MeuPadraoDayView
                      rotinas={rotinas}
                      statusById={meuPadraoStatusById}
                      busyTemplateId={busyTemplateId}
                      compact={compact}
                      alerts={viewingSubordinatePadrao ? [] : upcomingAlerts}
                      onOpenTask={handleOpenMeuPadraoTask}
                      readOnly={viewingSubordinatePadrao}
                      oee={meuPadraoOee}
                      ielScore={meuPadraoIel?.score ?? null}
                      selectedRoutineId={padraoTimelineSelectedId}
                      onSelectRoutine={setPadraoTimelineSelectedId}
                      executingRoutineId={soloTaskRotina?.template.id ?? null}
                    />
                  )}
                </div>
              </>
            ) : (
              <div className="min-h-0 flex-1 overflow-hidden">
                <LstMatrixMensal
                    cargo={viewCargo}
                    templates={templates}
                    execucoes={padraoRangeExec}
                    monthDates={periodView === 'semana' ? week.dates : month.dates}
                    userId={padraoViewUserId}
                    title={
                      periodView === 'semana'
                        ? 'Matriz Semanal Kamishibai'
                        : 'Matriz Mensal Kamishibai'
                    }
                    subtitle={
                      periodView === 'semana'
                        ? `${ddmm(week.start)} – ${ddmm(week.end)} · ${LST_CARGO_LABELS[viewCargo]}`
                        : `${monthLabel()} · ${LST_CARGO_LABELS[viewCargo]}`
                    }
                    unifiedChrome
                    periodView={periodView}
                    onPeriodChange={setPeriodView}
                    chromeTitle={
                      periodView === 'semana'
                        ? 'Matriz Semanal Kamishibai'
                        : 'Matriz Mensal Kamishibai'
                    }
                    chromeContextLine={`${meuPadraoRoleLabel}${userContext.departmentName ? ` · ${userContext.departmentName}` : ''} · ${meuPadraoDateLabel}`}
                    leaderOptions={padraoLeaderOptions}
                    selectedLeaderId={padraoViewUserId}
                    onLeaderChange={handlePadraoLeaderChange}
                    viewingSubordinate={viewingSubordinatePadrao}
                    onCellClick={handleCellClick}
                  onHourBlockClick={handleHourBlockClick}
                />
              </div>
            )}
          </section>
        )}

        {effectiveScreen === 'construtor' && (
          <TplWeeklyPlanner blocks={plannerBlocks} onMoveBlock={movePlannerBlock} />
        )}

        {effectiveScreen === 'auditoria' && (
          <TplLayerAuditPanel
            leaders={auditLeaders}
            selectedLeaderId={effectiveLideradoId}
            onSelectLeader={setSelectedLideradoId}
            templates={auditLeaderTemplates}
            execucoes={auditLeaderExec}
            weekDates={week.dates}
            today={today}
            auditorName={currentMember?.name ?? user?.name ?? 'Gestor'}
            lastFieldAudit={lastFieldAudit}
            maturityNote={maturityNotes[effectiveLideradoId ?? ''] ?? ''}
            onMaturityNoteChange={(note) => {
              if (!effectiveLideradoId) return;
              setMaturityNotes((prev) => ({ ...prev, [effectiveLideradoId]: note }));
            }}
            onApprove={() => void handleApprovePattern()}
            approving={createLayerMeetingMut.isPending}
            indicators={indicators}
            indicatorSnapshots={ielIndicatorSnapshots}
            correctiveActions={orgActions}
            actionsLoading={actionsLoading}
          />
        )}

        {effectiveScreen === 'diagnostico' && (
          <LstAnalyticsDashboard
            organizationId={organizationId ?? ''}
            isManager={canAudit || canConfigure}
            templates={templates}
            execucoes={analyticsExec}
            subjects={analyticsSubjects}
            selfUserId={userId}
            departments={departments}
            indicators={indicators}
            milestones={milestones}
            today={today}
            loading={analyticsLoading}
            leaders={execDashboardLeaders.map((l) => ({ userId: l.userId, name: l.name }))}
            correctiveActions={orgActions}
            hub={managementHub}
            onNavigate={setScreen}
          />
        )}

        {effectiveScreen === 'cadeia-ajuda' && (
          <div className="flex h-full min-h-0 flex-col">
            <div className="flex shrink-0 items-center justify-end px-1 pb-2">
              <div className="inline-flex overflow-hidden rounded-[5px] border border-slate-200 dark:border-slate-700">
                {([
                  { id: 'aderencia', label: 'Aderência' },
                  { id: 'matriz', label: 'Matriz Mensal' },
                ] as const).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setChainView(opt.id)}
                    aria-pressed={chainView === opt.id}
                    className={`px-3 py-1 text-[11px] font-semibold uppercase tracking-wide transition-colors duration-100 ${
                      chainView === opt.id
                        ? 'bg-[#2F6FED] text-white'
                        : 'bg-white text-slate-500 hover:bg-slate-50 dark:bg-[var(--bg-surface)] dark:text-slate-400 dark:hover:bg-slate-800'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="min-h-0 flex-1">
              {chainView === 'aderencia' ? (
                <CadeiaAjudaPanel
                  rows={chainRows}
                  weekDates={week.dates}
                  loading={chainLoading}
                  viewerCargo={leaderCargo}
                  onOpenDiagnostic={handleOpenAdherenceDiagnostic}
                  activeDiagnostic={
                    adherenceDiagnostic
                      ? { memberId: adherenceDiagnostic.userId, date: adherenceDiagnostic.date }
                      : null
                  }
                />
              ) : (
                <CadeiaMatrizMensal
                  liderados={liderados}
                  selectedId={effectiveLideradoId}
                  onSelect={setSelectedLideradoId}
                  templates={templates}
                  execucoes={lideradoMonthExec}
                  monthDates={month.dates}
                  monthLabel={monthLabel()}
                  loadingExec={lideradoMonthLoading}
                  onCellClick={handleCellClick}
                  onHourBlockClick={handleHourBlockClick}
                />
              )}
            </div>
          </div>
        )}

        {effectiveScreen === 'configuracao' && (
          <div className="flex min-h-0 flex-1 flex-col">
            <ConfiguracaoPanel
            organizationId={organizationId}
            templates={templates}
            members={members}
            indicators={indicators}
            departments={departments}
            currentUserId={userId}
            userContext={userContext}
            savingTemplate={savingTemplate}
            savingLink={savingLink}
            onCreate={handleCreateTemplate}
            onUpdate={handleUpdateTemplate}
            onDeactivate={handleDeactivateTemplate}
            onSetReportsTo={handleSetReportsTo}
            isAdmin={isAdmin}
            />
          </div>
        )}
      </div>

      <RotinaContextDrawer
        open={!!activeDrawerRotina}
        rotina={activeDrawerRotina}
        displayStatus={drawerDisplayStatus}
        organizationId={organizationId ?? ''}
        userId={userId ?? ''}
        members={members.map((m) => ({ id: m.id, name: m.name }))}
        indicators={indicators}
        execucoes={drawerPeriod.exec}
        periodStart={drawerPeriod.start}
        periodEnd={drawerPeriod.end}
        indicatorSnapshots={ielIndicatorSnapshots}
        liderados={liderados}
        revisoes={revisoes}
        revisoesLoading={revisoesLoading}
        desviosDoDia={desviosDoDia}
        saving={!!activeDrawerRotina && busyTemplateId === activeDrawerRotina.template.id}
        savingAta={savingReuniao}
        resolveUserName={(uid) => resolveExecutorName(uid) ?? 'Operador'}
        onClose={closeContextDrawer}
        onSubmitStatus={handleDrawerSubmitStatus}
        onSaveAta={
          activeDrawerRotina && isAtaColetivaContext(activeDrawerRotina.template)
            ? handleSaveAtaFromDrawer
            : undefined
        }
      />

      <DesvioModal
        open={!!desvioRotina}
        rotinaTitulo={desvioRotina?.template.title ?? ''}
        initialJustificativa={desvioRotina?.execucao?.justificativa_desvio ?? ''}
        indicatorName={desvioIndicatorMeta?.hasLink ? desvioIndicatorMeta.name : null}
        indicatorTarget={desvioIndicatorMeta?.target ?? null}
        indicatorUnit={desvioIndicatorMeta?.unit ?? ''}
        initialIndicatorValue={desvioRotina?.execucao?.indicator_realized_value ?? null}
        saving={busyTemplateId === desvioRotina?.template.id && !!desvioRotina}
        onClose={() => setDesvioRotina(null)}
        onSubmit={handleSubmitDesvio}
      />

      {execucaoIndicatorMeta?.hasLink && execucaoIndicatorRotina && (
        <ExecucaoIndicatorModal
          open
          rotinaTitulo={execucaoIndicatorRotina.template.title}
          indicator={execucaoIndicatorMeta}
          initialValue={execucaoIndicatorRotina.execucao?.indicator_realized_value ?? null}
          saving={busyTemplateId === execucaoIndicatorRotina.template.id}
          onClose={() => setExecucaoIndicatorRotina(null)}
          onSubmit={handleSubmitIndicatorExecucao}
        />
      )}

      <AdherenceLossDiagnosticDrawer
        open={!!adherenceDiagnostic}
        memberName={adherenceDiagnostic?.memberName ?? ''}
        date={adherenceDiagnostic?.date ?? today}
        oee={diagnosticOee ?? auditLeaderOee}
        pareto={diagnosticPareto}
        correctiveActions={diagnosticActions}
        raioX={diagnosticRaioX}
        loadingActions={actionsLoading}
        onClose={() => setAdherenceDiagnostic(null)}
        onOpenAction={() => navigate('/acoes')}
      />

      <LstKpiDrawer
        open={kpiDrawerOpen}
        onClose={() => setKpiDrawerOpen(false)}
        onExpand={() => {
          setKpiDrawerOpen(false);
          setScreen('diagnostico');
        }}
        cargoLabel={LST_CARGO_LABELS[cargo]}
        today={today}
        rotinas={rotinas ?? []}
        chainRows={hubChainRows ?? []}
        correctiveActions={orgActions ?? []}
        loading={analyticsLoading}
      />

      <ExecucaoCellDrawer
        open={standardDrillOpen}
        template={cellDrill?.template ?? null}
        code={cellDrill?.code ?? null}
        date={cellDrill?.date ?? null}
        execucao={cellDrill?.execucao ?? null}
        executorName={cellDrill?.execucao ? resolveExecutorName(cellDrill.execucao.user_id) : null}
        indicators={indicators}
        savingIndicator={busyTemplateId === cellDrill?.template.id}
        onSaveIndicatorValue={handleSaveDrawerIndicator}
        onClose={() => setCellDrill(null)}
        onOpenAction={() => navigate('/acoes')}
      />

      <HourBlockDrawer
        open={!!hourDrill}
        onClose={() => setHourDrill(null)}
        date={hourDrill?.cell.date ?? null}
        hourLabel={hourDrill?.cell.hourLabel ?? null}
        loadMin={hourDrill?.cell.loadMin ?? 0}
        loadState={hourDrill?.cell.state ?? null}
        activities={hourDrill?.cell.activities ?? []}
        resolveUserName={(uid) => resolveExecutorName(uid) ?? 'Operador'}
      />

      <ToastContainer />
    </ModuleLayout>
  );
};

const NoTemplates: React.FC<{ cargoLabel: string }> = ({ cargoLabel }) => (
  <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
    <p className="text-[13px] font-semibold text-slate-600 dark:text-slate-300">
      Nenhuma rotina padrão para o cargo {cargoLabel}.
    </p>
    <p className="max-w-sm text-[11px] text-slate-400">
      A gestão (Gerente/Diretor) pode cadastrar os padrões na aba Configuração ou rodar a migração
      <code className="mx-1 rounded bg-slate-100 px-1 dark:bg-slate-800">database_lst_leader_standard_work.sql</code>
      para semear exemplos.
    </p>
  </div>
);

export default RotinasLiderancaPage;
